import { useMemo, useState } from "react";
import { SelectInput } from "@/components/admin/AdminFields";
import {
  IconChevronDown,
  IconChevronUp,
  IconPlus,
  IconX,
} from "@/components/icons";
import {
  buildSubmissionsNestedBreakdown,
  type SubmissionsAnalyticsBucket,
  type SubmissionsAnalyticsField,
  type SubmissionsNestedGroup,
} from "@/lib/forms/submissionsTableView";
import {
  buildCityPastorDistrictCoverage,
  type CityPastorDistrictCoverageGroup,
} from "@/lib/forms/cityPastorDistrictAnalytics";
import { resolveFieldType } from "@/lib/forms/normalizeFieldType";
import type { CityDto, PastorDistrictDto } from "@/types/city";
import type { FormBuilderField } from "@/types/formBuilder";
import type { FormSubmissionDto } from "@/types/submission";

interface SubmissionsAnalyticsProps {
  fields: SubmissionsAnalyticsField[];
  groupableFields: FormBuilderField[];
  submissions: FormSubmissionDto[];
  getFieldValue: (submission: FormSubmissionDto, fieldId: number) => string;
  onSelectField?: (fieldId: number) => void;
  cities?: CityDto[];
  pastorDistricts?: PastorDistrictDto[] | null;
}

function fieldOptionLabel(field: FormBuilderField): string {
  return field.label.trim() || field.name || `Поле #${field.id}`;
}

const analyticsNumberFormatter = new Intl.NumberFormat("ru-RU", {
  maximumFractionDigits: 10,
});

function AnalyticsBars({ buckets }: { buckets: SubmissionsAnalyticsBucket[] }) {
  return (
    <ul className="min-w-0 max-w-full space-y-2 overflow-hidden">
      {buckets.map((bucket) => (
        <li key={bucket.label} className="min-w-0 max-w-full">
          <div className="mb-1 flex min-w-0 items-baseline justify-between gap-3 font-onest text-xs">
            <span
              className="min-w-0 truncate text-slate-700"
              title={bucket.label}
            >
              {bucket.label}
            </span>
            <span className="shrink-0 tabular-nums text-slate-500">
              {bucket.count}
              <span className="ml-1.5 text-slate-400">{bucket.percent}%</span>
            </span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full rounded-full bg-brand-500"
              style={{ width: `${Math.min(bucket.percent, 100)}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

function CityEarCoverageList({
  groups,
}: {
  groups: CityPastorDistrictCoverageGroup[];
}) {
  const [openGroups, setOpenGroups] = useState<Set<number>>(() => new Set());

  return (
    <div className="min-w-0 max-w-full">
      <h4 className="mb-2 font-onest text-xs font-semibold uppercase text-slate-500">
        ЕАР
      </h4>
      <div className="min-w-0 max-w-full divide-y divide-slate-100 border-y border-slate-100">
        {groups.map((group) => {
          const open = openGroups.has(group.id);
          return (
            <div key={group.id}>
              <button
                type="button"
                aria-expanded={open}
                onClick={() => {
                  setOpenGroups((current) => {
                    const next = new Set(current);
                    if (next.has(group.id)) next.delete(group.id);
                    else next.add(group.id);
                    return next;
                  });
                }}
                className="flex w-full min-w-0 items-center gap-2 py-2.5 text-left"
              >
                <IconChevronDown
                  size={16}
                  className={[
                    "shrink-0 text-slate-400 transition-transform",
                    open ? "rotate-0" : "-rotate-90",
                  ].join(" ")}
                />
                <span className="min-w-0 flex-1 truncate font-onest text-sm font-medium text-slate-800">
                  {group.name}
                </span>
                <span className="shrink-0 text-right font-onest text-[11px] text-slate-500">
                  <span className="block tabular-nums">
                    {group.filledCityCount} из {group.totalCityCount} городов
                  </span>
                  <span className="block tabular-nums text-slate-400">
                    Ответов: {group.responseCount}
                  </span>
                </span>
              </button>
              {open ? (
                <div className="max-h-72 overflow-y-auto border-t border-slate-100 bg-slate-50/60 px-3 py-1">
                  {group.cities.length > 0 ? (
                    <ul className="divide-y divide-slate-100">
                      {group.cities.map((city) => {
                        const filled = city.responseCount > 0;
                        return (
                          <li
                            key={city.id}
                            className="flex min-w-0 items-center justify-between gap-3 py-2"
                          >
                            <span className="min-w-0 truncate font-onest text-xs text-slate-700">
                              {city.name}
                            </span>
                            <span
                              className={[
                                "shrink-0 font-onest text-[11px] font-medium tabular-nums",
                                filled ? "text-emerald-700" : "text-slate-400",
                              ].join(" ")}
                            >
                              {filled
                                ? `Заполнено · ${city.responseCount}`
                                : "Не заполнено"}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="py-2 font-onest text-xs text-slate-400">
                      Нет привязанных городов
                    </p>
                  )}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NestedGroupList({
  groups,
  path,
  levelIndex,
  levelLabels,
  openGroups,
  onToggle,
}: {
  groups: SubmissionsNestedGroup[];
  path: string;
  levelIndex: number;
  levelLabels: string[];
  openGroups: Set<string>;
  onToggle: (key: string) => void;
}) {
  const isLeafLevel = groups.every((group) => group.children.length === 0);
  if (isLeafLevel) {
    return (
      <AnalyticsBars
        buckets={groups.map((group) => ({
          label: group.label,
          count: group.count,
          percent: group.percent,
        }))}
      />
    );
  }

  const childLabel = levelLabels[levelIndex + 1];

  return (
    <div
      className={
        path
          ? "min-w-0 max-w-full overflow-hidden rounded-lg border border-slate-200 bg-white"
          : "min-w-0 max-w-full overflow-hidden"
      }
    >
      {groups.map((group) => {
        const key = path ? `${path}\0${group.label}` : group.label;
        const open = openGroups.has(key);
        return (
          <div key={key} className="border-b border-slate-100 last:border-b-0">
            <button
              type="button"
              aria-expanded={open}
              onClick={() => onToggle(key)}
              className="flex w-full min-w-0 items-center gap-2 px-3 py-2.5 text-left hover:bg-slate-50"
            >
              <IconChevronDown
                size={16}
                className={[
                  "shrink-0 text-slate-400 transition-transform",
                  open ? "rotate-0" : "-rotate-90",
                ].join(" ")}
              />
              <span
                className="min-w-0 flex-1 truncate font-onest text-sm font-medium text-slate-800"
                title={group.label}
              >
                {group.label}
              </span>
              <span className="shrink-0 font-onest text-xs tabular-nums text-slate-500">
                {group.count}
                <span className="ml-1.5 text-slate-400">{group.percent}%</span>
              </span>
            </button>
            {open ? (
              <div className="min-w-0 max-w-full overflow-hidden border-t border-slate-100 bg-slate-50/50 px-3 py-3 pl-7">
                {childLabel ? (
                  <p className="mb-2 font-onest text-[11px] font-medium uppercase tracking-wide text-slate-400">
                    {childLabel}
                  </p>
                ) : null}
                <NestedGroupList
                  groups={group.children}
                  path={key}
                  levelIndex={levelIndex + 1}
                  levelLabels={levelLabels}
                  openGroups={openGroups}
                  onToggle={onToggle}
                />
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export function SubmissionsAnalytics({
  fields,
  groupableFields,
  submissions,
  getFieldValue,
  onSelectField,
  cities = [],
  pastorDistricts,
}: SubmissionsAnalyticsProps) {
  const nestFields = groupableFields.filter((field) => field.id != null);
  const [levelFieldIds, setLevelFieldIds] = useState<number[]>(() =>
    nestFields
      .slice(0, Math.min(2, nestFields.length))
      .map((field) => field.id!),
  );
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set());

  const unusedFields = nestFields.filter(
    (field) => field.id != null && !levelFieldIds.includes(field.id),
  );
  const levelLabels = levelFieldIds.map((fieldId) => {
    const field = nestFields.find((item) => item.id === fieldId);
    return field ? fieldOptionLabel(field) : `Поле #${fieldId}`;
  });

  const nested = useMemo(
    () =>
      buildSubmissionsNestedBreakdown(
        submissions,
        levelFieldIds,
        getFieldValue,
      ),
    [getFieldValue, levelFieldIds, submissions],
  );
  const earCoverageByFieldId = useMemo(() => {
    const result = new Map<number, CityPastorDistrictCoverageGroup[]>();
    if (!pastorDistricts || cities.length === 0) return result;

    for (const field of nestFields) {
      if (field.id == null || resolveFieldType(field.type) !== "city") continue;
      result.set(
        field.id,
        buildCityPastorDistrictCoverage(
          submissions,
          field,
          nestFields,
          cities,
          pastorDistricts,
        ),
      );
    }
    return result;
  }, [cities, nestFields, pastorDistricts, submissions]);

  const resetOpenGroups = () => setOpenGroups(new Set());

  const updateLevel = (index: number, fieldId: number) => {
    setLevelFieldIds((current) => {
      const next = [...current];
      next[index] = fieldId;
      return next;
    });
    resetOpenGroups();
  };

  const moveLevel = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= levelFieldIds.length) return;
    setLevelFieldIds((current) => {
      const next = [...current];
      const currentId = next[index]!;
      next[index] = next[target]!;
      next[target] = currentId;
      return next;
    });
    resetOpenGroups();
  };

  const addLevel = () => {
    const nextField = unusedFields[0];
    if (nextField?.id == null) return;
    setLevelFieldIds((current) => [...current, nextField.id!]);
    resetOpenGroups();
  };

  const removeLevel = (index: number) => {
    setLevelFieldIds((current) =>
      current.length <= 1
        ? current
        : current.filter((_, itemIndex) => itemIndex !== index),
    );
    resetOpenGroups();
  };

  const optionsForLevel = (index: number) =>
    nestFields.filter(
      (field) =>
        field.id === levelFieldIds[index] ||
        unusedFields.some((item) => item.id === field.id),
    );

  if (fields.length === 0) {
    return (
      <div className="px-4 py-10 text-center">
        <p className="font-onest text-sm text-slate-600">
          Нет полей для аналитики. Подходят числовые поля, город, выбор и
          список.
        </p>
      </div>
    );
  }

  return (
    <div className="min-w-0 max-w-full space-y-6 overflow-x-hidden p-3 sm:p-4">
      {nestFields.length > 0 ? (
        <section className="min-w-0 max-w-full rounded-xl border border-slate-200 bg-slate-50/40 p-3 sm:p-4">
          <div className="mb-3">
            <h3 className="font-onest text-sm font-semibold text-ink">
              Вложенная разбивка
            </h3>
            <p className="mt-0.5 font-onest text-xs text-slate-500">
              Каждая строка — внутри предыдущей. Стрелками меняйте порядок.
            </p>
          </div>

          <div className="mb-4 w-full min-w-0 max-w-xl overflow-hidden rounded-lg border border-slate-200 bg-white px-2 py-2 sm:px-3">
            {levelFieldIds.map((fieldId, index) => {
              const isLast = index === levelFieldIds.length - 1;
              return (
                <div
                  key={`${fieldId}-${index}`}
                  className="flex min-w-0 max-w-full items-stretch gap-2"
                >
                  <div className="flex w-5 shrink-0 flex-col items-center">
                    <span
                      className={[
                        "w-px flex-1 bg-slate-200",
                        index === 0 ? "invisible" : "",
                      ].join(" ")}
                    />
                    <span className="size-2 shrink-0 rounded-full bg-brand-500" />
                    <span
                      className={[
                        "w-px flex-1 bg-slate-200",
                        isLast && unusedFields.length === 0 ? "invisible" : "",
                      ].join(" ")}
                    />
                  </div>
                  <div className="flex min-w-0 flex-1 items-center gap-1 py-1">
                    <span className="w-4 shrink-0 font-onest text-[11px] tabular-nums text-slate-400">
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1 overflow-hidden">
                      <SelectInput
                        id={`nested-level-${index}`}
                        size="compact"
                        value={String(fieldId)}
                        onChange={(value) => updateLevel(index, Number(value))}
                      >
                        {optionsForLevel(index).map((field) => (
                          <option key={field.id} value={field.id}>
                            {fieldOptionLabel(field)}
                          </option>
                        ))}
                      </SelectInput>
                    </div>
                    <div className="flex shrink-0 items-center">
                      <button
                        type="button"
                        title="Выше"
                        disabled={index === 0}
                        onClick={() => moveLevel(index, -1)}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:invisible"
                      >
                        <IconChevronUp size={14} />
                      </button>
                      <button
                        type="button"
                        title="Ниже"
                        disabled={isLast}
                        onClick={() => moveLevel(index, 1)}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:invisible"
                      >
                        <IconChevronDown size={14} />
                      </button>
                      {levelFieldIds.length > 1 ? (
                        <button
                          type="button"
                          title="Убрать"
                          onClick={() => removeLevel(index)}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        >
                          <IconX size={14} />
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
            {unusedFields.length > 0 ? (
              <div className="flex items-stretch gap-2">
                <div className="flex w-5 shrink-0 flex-col items-center">
                  <span className="h-2 w-px bg-slate-200" />
                  <span className="size-2 shrink-0 rounded-full border border-dashed border-brand-400 bg-white" />
                  <span className="invisible w-px flex-1" />
                </div>
                <div className="py-1">
                  <button
                    type="button"
                    onClick={addLevel}
                    className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 font-onest text-xs font-medium text-brand-700 hover:bg-brand-50"
                  >
                    <IconPlus size={14} />
                    Добавить внутрь
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          {nested.length > 0 ? (
            <div className="min-w-0 max-w-full overflow-hidden rounded-lg border border-slate-200 bg-white">
              {levelLabels[0] ? (
                <p className="border-b border-slate-100 px-3 py-2 font-onest text-[11px] font-medium uppercase tracking-wide text-slate-400">
                  {levelLabels[0]}
                </p>
              ) : null}
              <NestedGroupList
                groups={nested}
                path=""
                levelIndex={0}
                levelLabels={levelLabels}
                openGroups={openGroups}
                onToggle={(key) => {
                  setOpenGroups((prev) => {
                    const next = new Set(prev);
                    if (next.has(key)) next.delete(key);
                    else next.add(key);
                    return next;
                  });
                }}
              />
            </div>
          ) : null}
        </section>
      ) : null}

      <div className="grid min-w-0 max-w-full gap-4 lg:grid-cols-2">
        {fields.map((field) => {
          const earCoverage = earCoverageByFieldId.get(field.fieldId);
          return (
            <section
              key={field.fieldId}
              className="min-w-0 max-w-full overflow-hidden rounded-xl border border-slate-200 bg-white p-3 sm:p-4"
            >
              <div className="mb-3 flex min-w-0 flex-col items-start gap-2 sm:flex-row sm:items-start sm:justify-between">
                <h3
                  className="min-w-0 max-w-full flex-1 break-words font-onest text-sm font-semibold leading-snug text-ink sm:truncate"
                  title={field.label}
                >
                  {field.label}
                </h3>
                {onSelectField && !field.numericSummary ? (
                  <button
                    type="button"
                    onClick={() => onSelectField(field.fieldId)}
                    className="max-w-full shrink-0 whitespace-normal text-left font-onest text-[11px] font-medium text-brand-700 hover:underline sm:text-right"
                  >
                    Группировать в списке
                  </button>
                ) : null}
              </div>
              {field.numericSummary ? (
                <div className="py-1">
                  <p className="font-onest text-[11px] font-medium uppercase text-slate-400">
                    Сумма
                  </p>
                  <p className="mt-1 break-words font-onest text-2xl font-semibold tabular-nums text-ink">
                    {analyticsNumberFormatter.format(field.numericSummary.sum)}
                  </p>
                  <p className="mt-1 font-onest text-xs text-slate-500">
                    Учтено значений: {field.numericSummary.valueCount} из{" "}
                    {field.numericSummary.totalCount}
                  </p>
                </div>
              ) : (
                <>
                  {earCoverage ? (
                    <>
                      <CityEarCoverageList groups={earCoverage} />
                      <div className="mt-4 border-t border-slate-100 pt-4">
                        <p className="mb-2 font-onest text-xs font-semibold uppercase text-slate-500">
                          Города
                        </p>
                        <AnalyticsBars buckets={field.buckets} />
                      </div>
                    </>
                  ) : (
                    <AnalyticsBars buckets={field.buckets} />
                  )}
                </>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
